import { env } from "@/lib/env";

export class ApiError extends Error {
  status: number;
  requiredPlan?: string;

  constructor(message: string, status: number, requiredPlan?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.requiredPlan = requiredPlan;
  }
}

export function getAccessToken() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(env.tokenStorageKey);
}

export function setAccessToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem(env.tokenStorageKey, token);
  else localStorage.removeItem(env.tokenStorageKey);
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
};

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function isSafeRetryMethod(method: string | undefined) {
  const m = (method || "GET").toUpperCase();
  return m === "GET" || m === "HEAD";
}

export async function api<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers, ...rest } = options;
  const token = getAccessToken();
  const method = rest.method;
  // Brief retries cover node --watch restarts; never auto-retry mutating calls.
  const attempts = isSafeRetryMethod(method) ? 3 : 1;

  let res: Response | undefined;
  for (let i = 0; i < attempts; i += 1) {
    try {
      res = await fetch(`${env.apiUrl}${path}`, {
        credentials: "include",
        ...rest,
        headers: {
          ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...headers,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      break;
    } catch {
      if (rest.signal?.aborted) break;
      if (i < attempts - 1) await sleep(350 * (i + 1));
    }
  }

  if (!res) {
    throw new ApiError(
      `Cannot reach API at ${env.apiUrl}. Backend may be restarting — wait a moment and retry.`,
      0,
    );
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(
      (data as { error?: string }).error || res.statusText || "Request failed",
      res.status,
      (data as { requiredPlan?: string }).requiredPlan,
    );
  }

  if (
    data &&
    typeof data === "object" &&
    "token" in data &&
    typeof (data as { token: unknown }).token === "string"
  ) {
    setAccessToken((data as { token: string }).token);
  }

  return data as T;
}

export function googleAuthUrl(next?: string) {
  const url = new URL(`${env.apiUrl}${env.paths.authGoogle}`);
  if (next) url.searchParams.set("next", next);
  return url.toString();
}
